// ============================================
// Auth Guard - Redirects based on auth, disclaimer, and subscription state
// ============================================

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { fetchProfile } from '../data/cloud';
import { useCloudQuery } from '../data/hooks';

const LoadingScreen: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100dvh' }}>
    <div className="loading-spinner" />
  </div>
);

/**
 * Inner guard that runs only when we know a session exists. Split so the
 * useCloudQuery hook is never called without a valid user id (fetchProfile
 * throws "Not authenticated" if called pre-session). Reuses the shared
 * cache so the profile is fetched at most once per user and every guarded
 * navigation after that is a synchronous cache read.
 */
const AuthedGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: profile, loading: profileLoading } = useCloudQuery(
    'user_profiles',
    fetchProfile,
    null,
  );

  if (profileLoading) {
    return <LoadingScreen />;
  }

  if (profile && !profile.disclaimerAcknowledgedAt) {
    return <Navigate to="/disclaimer" replace />;
  }

  return <>{children}</>;
};

export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return <AuthedGuard>{children}</AuthedGuard>;
};
